import React, { useEffect, useState } from 'react';
import { useUserAgent } from 'next-useragent';
import nookies, { setCookie } from 'nookies';
import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { SearchStoreProvider } from '../providers/SearchStoreProvider';
import { RootStoreProvider } from '../providers/RootStoreProvider';
import { Layout } from '../components/Layout';
import { SearchPage } from '../components/SearchPage';
import { apiProxy } from '../lib/api';
import { ModalReload } from '../components/ModalReload';
import { yandexUserLayer } from '../lib/yandexDataLayer';
import { useHiddenWithCookie } from '../hooks/useHiddenWithCookie';
import { YaAndGaResultPage } from '../lib/YaAndGaHelper';
import { logger } from '../lib/logger';
import { containsSea } from '../utils/containSea';

const BannerTop = dynamic(() =>
  import('../components/CookieBanner').then(mod => mod.CookieBanner),
);

export default function ResultPage({
  uaString,
  store: { user, banners, cookies, popularPopdirs },
  canonicalUrl,
}) {
  const ua = useUserAgent(uaString || window.navigator.userAgent);
  const { isMobile, isTablet } = ua;
  const router = useRouter();
  const { type } = router.query;
  const isSea = type === 'sea' || router.asPath.startsWith('/sea_cruise');

  useEffect(() => {
    // Яндекс метрика - информация о пользователи и типе страницы
    yandexUserLayer(user?.user?.id, user?.user?.group, isSea);
    // YaAndGaResultPage(isSea ? 'isSeaPage' : 'isRiverPage');
  }, [isSea]);

  const [closeBanners, setCloseBanners] = useState(
    cookies?.closeBanner !== undefined || cookies?.closeBanner === 'false',
  );
  const currentDate = dayjs();
  const timeToCookie = currentDate.add(1, 'h');
  setCookie(null, 'closeBanner', 'false');
  const openBanner = () => {
    setCloseBanners(false);
    setCookie(null, 'closeBanner', 'false');
  };
  useHiddenWithCookie(openBanner);

  return (
    <SearchStoreProvider hydrationData={{ user }}>
      <RootStoreProvider hydrationData={{ user }}>
        {/* Не переписываем на NextSeo, Head специально используется для правильной работы мета-тега robots */}
        <Head>
          <title>Каталог</title>
          <meta name="description" content="Каталог" />
          <meta name="robots" content="noindex, nofollow" />
          <link rel="shortcut icon" href="/favicon.ico?v=2" />
          <link rel="canonical" href={canonicalUrl} />
        </Head>

        <Layout
          isMobile={isMobile}
          isTablet={isTablet}
          showTopContent={!closeBanners}
          popularPopdirs={popularPopdirs}
          topContent={
            <div>
              <BannerTop
                isOpen={closeBanners}
                onOpen={setCloseBanners}
                timeToCookie={timeToCookie}
                isMobile={isMobile}
                isTablet={isTablet}
              />
            </div>
          }
        >
          <div className="container--block">
            <SearchPage
              isMobile={isMobile}
              isSea={isSea}
              urlBanners={banners}
            />
          </div>
        </Layout>
        <ModalReload />
      </RootStoreProvider>
    </SearchStoreProvider>
  );
}

export const getServerSideProps = async ctx => {
  const cookies = nookies.get(ctx);
  const { token } = cookies;

  const canonicalUrl = `https://kruiz.online/result`;
  const hasSea = containsSea(ctx.req.url);
  try {
    const user = await apiProxy
      .get(`ApiUsers/check_token/${token ?? ''}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      })
      .then(resp => resp.data);
    const responses = await Promise.allSettled([
      apiProxy.get(`ApiBanners/getResultBanners`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
      apiProxy.get(`ApiPopdirNew/getHeaderPopdir/${hasSea}`, {
        headers: {
          'x-request-id': ctx.req.headers['x-request-id'] ?? 'qwert',
          ym_uid: cookies._ym_uid || 'ym_cid not found',
        },
      }),
    ]);
    const [banners, popularPopdirs] = responses.map(req =>
      req.status === 'fulfilled' ? req.value.data ?? null : null,
    );
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      level: 'debug',
      ym_uid: cookies._ym_uid,
      message: 'result page has been created',
    });
    return {
      props: {
        store: { user, banners, cookies, popularPopdirs },
        uaString: ctx.req.headers['user-agent'],
        canonicalUrl,
      },
    };
  } catch (error) {
    logger({
      additionalFields: {
        url: ctx.resolvedUrl,
      },
      error,
      level: 'error',
      message: 'result page was not created',
    });
    return {
      redirect: {
        destination: '/503',
        permanent: true,
      },
    };
  }
};
